import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { login, type UserPublic } from "@/api/auth-api";
import { OrgBrand } from "@/components/OrgBrand";
import { clearSessionByPrefix } from "@/hooks/useSessionState";
import { useAuthStore, type AuthUser } from "@/store/auth-store";
import { landingPathFor } from "@/lib/landing";
import { APP_NAME } from "@/lib/branding";

// Map the wire-format UserPublic to the slimmer in-memory AuthUser the
// store carries. The store deliberately omits non-identity fields
// (license_number, last_login_at, created_at) — they live in the user
// list cache, not the auth context.
function toAuthUser(u: UserPublic): AuthUser {
  return {
    id: u.id,
    username: u.username,
    role: u.role,
    full_name: u.full_name,
    email: u.email,
    active: u.active,
  };
}

function extractDetail(err: unknown): string {
  // Axios error → response.data.detail when the backend rejected with
  // a structured 4xx; fall back to the bare message otherwise.
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    .response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation arrays — flatten the first message.
    const first = detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return "Sign in failed. Please try again.";
}

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ username: username.trim(), password });
      // Start every login from the default view. Worklist filters live in
      // tab-scoped sessionStorage so they survive an F5, but that also
      // means a log-out-then-in on the same tab would inherit the previous
      // session's filters — clear them so a new day's login opens on Today.
      clearSessionByPrefix("worklist.");
      const authUser = toAuthUser(res.user);
      setSession(res.access_token, authUser);
      // Not every role belongs on the worklist -- a test candidate has no
      // permission to see it. See lib/landing.ts.
      navigate(landingPathFor(authUser.role), { replace: true });
    } catch (err) {
      setError(extractDetail(err));
      // Clear the password field but keep the username — most failed
      // logins are typos in the password, not the username.
      setPassword("");
      setSubmitting(false);
    }
  };

  const canSubmit =
    username.trim().length > 0 && password.length > 0 && !submitting;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div
        className="w-full max-w-sm rounded-xl border border-border bg-background-card p-6 shadow-theme-md"
        data-cy="login-card"
      >
        <div className="mb-4 flex justify-center">
          <OrgBrand variant="login" />
        </div>
        <h1 className="text-xl font-semibold text-foreground-heading">{APP_NAME}</h1>
        <p className="mt-1 text-sm text-foreground-muted">Sign in to continue</p>

        <form
          className="mt-6 space-y-4"
          data-testid="login-form"
          data-cy="login-form"
          onSubmit={onSubmit}
          noValidate
        >
          <div>
            <label
              htmlFor="username"
              className="text-xs font-medium uppercase tracking-wider text-foreground-muted"
            >
              Username
            </label>
            <input
              id="username"
              name="username"
              data-cy="username"
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              className="sk-input mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="text-xs font-medium uppercase tracking-wider text-foreground-muted"
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              data-cy="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              className="sk-input mt-1"
            />
          </div>

          {error ? (
            <div
              role="alert"
              data-cy="login-error"
              className="rounded-md border-l-4 border-status-urgent bg-status-urgent-bg px-3 py-2 text-xs text-foreground"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            data-cy="login-btn"
            disabled={!canSubmit}
            className="sk-btn sk-btn-primary w-full"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
